import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Typography,
    Box,
    IconButton,
} from '@mui/material';
import {
    Close as CloseIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
    FormValues,
    transformFormValuesToPayload,
    formatBulkUploadMessage
} from './logic';
import {
    StyledTabs,
    StyledTab,
    dialogPaperStyles,
    headerStyles,
    headerTitleStyles,
    headerIconStyles,
    modalContentStyles,
    scrollableContentStyles,
} from './styles';
import ManualVerificationForm from './ManualVerificationForm';
import CsvUploadForm from './CsvUploadForm';
import { VerificationQueries } from '../../../data/verification/verification.queries';

interface CreateFormProps {
    open: boolean;
    onClose: () => void;
}

const CreateForm: React.FC<CreateFormProps> = ({ open, onClose }) => {
    const [activeTab, setActiveTab] = useState(0);
    const { enqueueSnackbar } = useSnackbar();
    const createVerificationMutation = VerificationQueries.useCreateVerification();
    const bulkUploadMutation = VerificationQueries.useBulkUploadVerifications();

    const handleManualVerificationSubmit = async (values: FormValues) => {
        const payload = transformFormValuesToPayload(values);

        await createVerificationMutation.mutateAsync(payload, {
            onSuccess: (data) => {
                console.log('Verification created successfully:', data);
                enqueueSnackbar('Verification created successfully!', { variant: 'success' });
                onClose();
            },
            onError: (error: Error) => {
                console.error('Failed to create verification:', error);
                handleError(`Failed to create verification: ${error.message}`);
            },
        });
    };

    const handleCsvUpload = async (file: File) => {
        await bulkUploadMutation.mutateAsync(file, {
            onSuccess: (data) => {
                console.log('Bulk upload completed:', data);
                const message = formatBulkUploadMessage(data);
                enqueueSnackbar(message, { variant: 'success' });
                onClose();
            },
            onError: (error: Error) => {
                console.error('Bulk upload failed:', error);
                handleError(`Upload failed: ${error.message}`);
            },
        });
    };

    const handleTabChange = (newValue: number) => {
        setActiveTab(newValue);
    };

    const handleError = (message: string) => {
        enqueueSnackbar(message, { variant: 'error' });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            PaperProps={{ sx: dialogPaperStyles }}
            sx={{
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(10, 18, 48, 0.75)',
                    backdropFilter: 'blur(0.3px)',
                }
            }}
        >
            <DialogContent sx={modalContentStyles}>
                <Box sx={headerStyles}>
                    <Typography variant="h5" sx={headerTitleStyles}>
                        Add a business
                    </Typography>
                    <IconButton onClick={onClose} sx={headerIconStyles}>
                        <CloseIcon sx={{ color: '#9e9e9e' }} />
                    </IconButton>
                </Box>

                {/* Tabs */}
                <StyledTabs
                    value={activeTab}
                    onChange={(_, newValue) => handleTabChange(newValue)}
                    sx={{ px: 3 }}
                >
                    <StyledTab label="Manual Input" />
                    <StyledTab label="CSV Upload" />
                </StyledTabs>

                {/* Scrollable Content */}
                <Box sx={scrollableContentStyles}>
                    {/* Manual Input Tab */}
                    {activeTab === 0 && (
                        <ManualVerificationForm
                            onSubmit={handleManualVerificationSubmit}
                            onCancel={onClose}
                            isLoading={createVerificationMutation.isLoading}
                        />
                    )}

                    {/* CSV Upload Tab */}
                    {activeTab === 1 && (
                        <CsvUploadForm
                            onUpload={handleCsvUpload}
                            onCancel={onClose}
                            isLoading={bulkUploadMutation.isLoading}
                            onError={handleError}
                        />
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default CreateForm;
